import { ChevronDown, ChevronUp } from "lucide-react";
import React, { createContext, useContext, useState } from "react";
import { useTopic } from "@client/hooks/useTopic";
import { nickname } from "@client/utils/nickname";

interface HidingWrapperContextType {
  /** True když je sekce rozbalená (children se renderují). Viz Fáze 6 (visibility-aware fetching). */
  visible: boolean;
}

const HidingWrapperContext = createContext<HidingWrapperContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalHidingWrapper = (): HidingWrapperContextType | undefined => useContext(HidingWrapperContext);

/**
 * Refactor 2026-05 (CHART-DATA-REFACTOR Fáze 4): explicit `topicId` prop místo
 * křehkého recursive Children scan. Také vystavuje `visible` v contextu pro
 * children, které chtějí pause fetch při collapsed.
 */
const HidingWrapper: React.FC<HidingWrapperProps> = ({
  topicId,
  showInitially = false,
  onlyVisibility = false,
  children,
}) => {
  const [showAll, setShowAll] = useState(showInitially);
  const { topic } = useTopic(topicId ?? "");

  const toggleVisibility = () => setShowAll(!showAll);

  // Pro `onlyVisibility` jsou children stále renderovány (jen visually hidden) —
  // visible je `showAll`. Pro standard mode jsou children unmount při collapsed.
  const childrenVisible = onlyVisibility ? showAll : showAll;
  const contextValue = { visible: childrenVisible };

  return (
    <div className="rounded-lg bg-muted/30 p-2 m-1">
      <div className="flex items-center mb-2 gap-2">
        <h2 className="text-sm font-medium text-muted-foreground">{topicId ? nickname(topic) : "—"}</h2>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          onClick={toggleVisibility}
          aria-label={showAll ? "Skrýt" : "Zobrazit"}
        >
          {showAll ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>
      <HidingWrapperContext.Provider value={contextValue}>
        {onlyVisibility ? (
          <div className={showAll ? "" : "hidden"}>{children}</div>
        ) : (
          showAll && <div>{children}</div>
        )}
      </HidingWrapperContext.Provider>
    </div>
  );
};

export default HidingWrapper;
